/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type {
  ChartSeriesData,
  Info,
  ChartContextMenuContext,
  Group
} from '../../utils/UNSAFE_visTypes/chart';
import { getInfo } from '../../utils/UNSAFE_visUtils';
import type { Ref } from 'preact/hooks';

export function getChartContextMenuContext<K, D>(
  focusedItemInfo: Info,
  gesture: 'mouse' | 'touch' | 'keyboard',
  target: HTMLElement,
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  rootRef: Ref<HTMLElement>,
  xAxisTitle?: string,
  yAxisTitle?: string,
  getDataItem?: (seriesIndex: number, groupIndex: number) => D | undefined
) {
  let seriesIndex;
  let groupIndex;
  if (gesture === 'keyboard') {
    //We find the element where the position of the menu is going to be based of
    groupIndex = focusedItemInfo.groupIndex;
    seriesIndex = focusedItemInfo.seriesIndex;
  } else {
    //We search for the key using the target of the event
    const info = getInfo(rootRef, target);
    seriesIndex = info ? info.seriesIndex : undefined;
    groupIndex = info ? info.groupIndex : undefined;
  }
  let context;
  if (
    seriesIndex !== undefined &&
    groupIndex !== undefined &&
    getDataItem?.(seriesIndex, groupIndex)
  ) {
    context = {
      data: getDataItem(seriesIndex, groupIndex),
      groupIndex,
      seriesIndex,
      type: 'item'
    };
  } else if (seriesIndex !== undefined) {
    context = {
      data: series[seriesIndex],
      type: 'series'
    };
  } else if (groupIndex !== undefined) {
    context = {
      data: groups[groupIndex],
      type: 'xAxisTickLabel'
    };
  } else {
    const target = event?.target as HTMLElement;
    const text = target?.textContent;
    if ((text && gesture !== 'keyboard' && text === xAxisTitle) || text === yAxisTitle) {
      context = {
        type: 'axisTitle',
        axis: text === xAxisTitle ? 'x' : 'y'
      };
    } else {
      context = {
        type: 'background'
      };
    }
  }
  return {
    context: context as ChartContextMenuContext<K, D>
  };
}
